/**
 * Shared contract for the dedup id on the two conversions both the browser and
 * core-api report (`signup_completed`, `screener_submitted`).
 *
 * The ad platforms receive each of those twice — once from the browser pixel,
 * once from core-api's CAPI module — and collapse the pair on this id (Meta
 * `event_id`, Reddit `conversion_id`). Neither side mints it and neither side
 * carries it: both derive it from the entities the conversion is about, which
 * is what lets a report land days after the pixel and still deduplicate.
 *
 * The derivation lives here rather than once per side because a format
 * difference between the two has no symptom except the conversion silently
 * counting twice. There is one implementation, so there is nothing to diverge.
 *
 * Consumed by core-api's `ConversionReportingService` and by the apps/web signup
 * and apply routes. See `v4/features/conversions/conversions.md` for the wider
 * design, and `apps/web/src/lib/analytics/conversion-events.ts` for the catalog
 * these two event names come from.
 */

/**
 * The event name is part of the id because one request can carry both
 * conversions: a public application registers the account and files the
 * application together. Meta and Reddit deduplicate on event name plus id, so
 * namespacing is enough to keep the two apart.
 *
 * Ids are lower-cased so that the hex spelling of a Mongo id cannot decide
 * whether two reports pair up. core-api renders its ids with `toHexString()`,
 * which is always lower case; the browser reads a `projectId` off the URL,
 * which is whatever the inbound link spelled it as.
 *
 * Not hashed, deliberately. This is a join key, never a secret, and an opaque
 * digest is what makes a conversion unidentifiable in Events Manager while a
 * campaign is being debugged. A Mongo id is not sensitive beside the hashed
 * emails the same payload already carries.
 */
function conversionDedupId(name: string, ids: string[]): string {
  return [name, ...ids.map((id) => id.toLowerCase())].join(":");
}

/** Derived from the account the signup created. */
export function signupCompletedDedupId(userId: string): string {
  return conversionDedupId("signup_completed", [userId]);
}

/**
 * Derived from the project and the applicant, not from the `ScreenerResponse`
 * that carries the application.
 *
 * A participant applies to a given project once: re-applying, withdrawing and
 * being rejected all move `visibility` on the one response document rather than
 * adding a second, so the pair is as unique per application as the response id
 * is. The pair is also stable in the one case the response id is not. core-api
 * mints that `_id` inside the request that files the application, so a
 * participant who abandons a submit and retries reaches a second `_id` — and
 * two counted conversions for one applicant, which is the exact outcome a
 * shared id exists to prevent.
 */
export function screenerSubmittedDedupId(
  projectId: string,
  userId: string,
): string {
  return conversionDedupId("screener_submitted", [projectId, userId]);
}
